/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAccessibleDropdown, useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import isUndefined from 'lodash/isUndefined';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ICON_CHEVRON_RIGHT, ICON_CLOSE } from '../../Icons';
import {
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  Button,
} from '../Button';
import { CheckboxList, CheckboxRadioProps, RadioButtons } from '../FormFields/fields/CheckboxRadio';
import { SdsIcon } from '../SdsIcon';
import { ChipFilterProps, chipFilterDefaultProps } from './ChipFilter.props';

/**
 * Chip filter SDS component.
 */
export const ChipFilter = (props: ChipFilterProps): JSX.Element => {
  const {
    filterHeader,
    dataId,
    icon,
    simplifyRadioFilters,
    classes,
    isDisabled,
    checkboxListProps,
    setFilters,
    showClearAll,
    counter,
    badge,
    type,
    filters,
    setChosenRadioOptionHandler,
    chosenRadioOption,
    confirmRadioOptionHandler,
    confirmedRadioOption,
    ...rest
  } = {
    ...chipFilterDefaultProps,
    ...props,
  };

  const labels = {
    ...chipFilterDefaultProps.labels,
    ...(props.labels || {}),
  };

  const filterListRef = useRef<HTMLInputElement>(null);
  const filterRadioRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const uniqueId = useUniqueId();
  const wrapperId = `chip-filter-dropdown-wrapper--${uniqueId}`;
  const menuId = `chip-filter-dropdown--${uniqueId}`;
  const buttonId = `chip-filter-dropdown-button--${uniqueId}`;

  const { accessibleDropdownButtonProps, dropdownOpen, setDropdownOpen } = useAccessibleDropdown({
    dropdownSelector: `#${wrapperId}`,
    itemCount: filters.length,
  });

  useEffect(() => {
    if (simplifyRadioFilters && chosenRadioOption) {
      setDropdownOpen(false);
    }
  }, [chosenRadioOption, setDropdownOpen, simplifyRadioFilters]);

  // Checkbox type
  const handleChangeFilters = useCallback((): void => {
    const filterList = filterListRef.current as HTMLInputElement;
    setDropdownOpen(false);
    setFilters && setFilters(JSON.parse(filterList.value));
  }, [setDropdownOpen, setFilters]);

  const clearFilters = useCallback(() => {
    // Update the filters to all be unchecked and return the set
    const updated: ChipFilterProps['filters'] = filters.map((filter) => {
      filter.checked = false;
      return filter;
    });
    setFilters && setFilters(updated as []);
    setDropdownOpen(false);
  }, [filters, setDropdownOpen, setFilters]);

  const selectedCount = useMemo((): number => {
    return filters.filter((item) => item.checked === true).length;
  }, [filters]);

  const filterListProps: CheckboxRadioProps = {
    fieldLabel: `Checkbox label`,
    hideLabel: true,
    id: `chip-filter-${uniqueId}`,
    name: `chip-filter-${uniqueId}`,
    ...checkboxListProps,
    options: filters,
  };

  // Radio type
  const handleChangeRadioFilters = useCallback((): void => {
    chosenRadioOption && confirmRadioOptionHandler && confirmRadioOptionHandler(chosenRadioOption);
    setDropdownOpen(false);
  }, [chosenRadioOption, confirmRadioOptionHandler, setDropdownOpen]);

  const filterRadiosProps: CheckboxRadioProps = {
    fieldLabel: `Radio label`,
    hideLabel: true,
    id: `chip-radio-${uniqueId}`,
    name: `chip-radio-${uniqueId}`,
    options: filters,
    radioCallback: setChosenRadioOptionHandler,
    tabOverLabels: simplifyRadioFilters,
    value: chosenRadioOption,
  };

  const selectedOptionInitial = useMemo((): string | undefined => {
    return confirmedRadioOption && confirmedRadioOption[0];
  }, [confirmedRadioOption]);

  const chipFilterButtonText = useMemo(() => {
    if (simplifyRadioFilters) {
      return chosenRadioOption || labels.default;
    }
    return labels.default;
  }, [chosenRadioOption, labels.default, simplifyRadioFilters]);

  const filterButtonContent = (
    <div className="chip-filter__btn-content">
      {icon} {chipFilterButtonText}{' '}
      {type === 'radio' && badge && confirmedRadioOption && selectedOptionInitial && (
        <span className="chip-filter__btn-counter">{selectedOptionInitial}</span>
      )}{' '}
      {type === 'checkbox' && counter && selectedCount > 0 && (
        <span className="chip-filter__btn-counter">{selectedCount}</span>
      )}{' '}
      <span className={dropdownOpen ? `open` : `closed`}>{ICON_CHEVRON_RIGHT}</span>
    </div>
  );

  return (
    <div
      className={clsx(`chip-filter`, classes, {
        [`chip-filter--no-icon`]: isUndefined(icon),
        [`chip-filter--open`]: dropdownOpen,
        ['chip-filter--radio-simplified']: simplifyRadioFilters,
      })}
      id={wrapperId}
      ref={dropdownRef}
      {...rest}
    >
      <Button
        {...accessibleDropdownButtonProps}
        aria-controls={menuId}
        aria-label={labels.default}
        classes={clsx(`chip-filter__btn`, {
          [`active`]: selectedCount > 0 || selectedOptionInitial,
        })}
        customContent={filterButtonContent}
        dataId={dataId}
        format={BUTTON_FORMAT_SOFT}
        id={buttonId}
        isDisabled={isDisabled}
        theme={BUTTON_THEME_DEFAULT}
      />
      {dropdownOpen && (
        <div
          aria-labelledby={buttonId}
          className={clsx(`chip-filter__dropdown`, { [`chip-filter__dropdown--open`]: dropdownOpen })}
          id={menuId}
          role="menu"
        >
          <div className="chip-filter__dropdown-header">
            <h3 className="chip-filter__dropdown-title">{filterHeader || labels.default}</h3>
            <button className="chip-filter__dropdown-close-btn" onClick={() => setDropdownOpen(false)}>
              <SdsIcon svg={ICON_CLOSE} />
            </button>
          </div>
          <div>
            <div className="chip-filter__dropdown-body">
              {type === 'radio' ? (
                <RadioButtons {...filterRadiosProps} ref={filterRadioRef} />
              ) : (
                <CheckboxList {...filterListProps} ref={filterListRef} />
              )}
            </div>
            <div
              className={clsx('chip-filter__dropdown-btn-container', {
                ['chip-filter__dropdown-btn-container--centered']: showClearAll,
              })}
            >
              {type === 'checkbox' && showClearAll && (
                <Button
                  classes="chip-filter__dropdown-btn chip-filter__dropdown-btn--clear-all"
                  format={BUTTON_FORMAT_SOFT}
                  onClick={clearFilters}
                  text={labels.clearAll}
                  theme={BUTTON_THEME_DEFAULT}
                />
              )}
              <Button
                classes="chip-filter__dropdown-btn chip-filter__dropdown-btn--apply"
                format={BUTTON_FORMAT_STRONG}
                onClick={type === 'radio' ? handleChangeRadioFilters : handleChangeFilters}
                text={labels.button}
                theme={BUTTON_THEME_PRIMARY}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
